import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Heart, ChatCircle, Eye, ArrowLeft, PaperPlaneTilt, Trash, BookmarkSimple, Books, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import NewsletterSignup from "@/components/NewsletterSignup";
import GoogleAd from "@/components/GoogleAd";

export default function ArticleDetail() {
  const { username, slug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [busyComment, setBusyComment] = useState(false);
  const [liking, setLiking] = useState(false);
  const [bookmarking, setBookmarking] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/sites/${username}/articles/${slug}`);
        setData(data);
        document.title = `${data.article.title} — ${data.author.name}`;
        const cm = await api.get(`/articles/${data.article.id}/comments`);
        setComments(cm.data);
      } catch (e) { setErr("Article not found"); }
    })();
  }, [username, slug]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <div className="eyebrow text-muted-foreground">404</div>
        <h1 className="font-serif text-5xl font-black tracking-tight mt-3">{err}</h1>
        <Link to={`/${username}`} className="mt-6 inline-flex items-center gap-2 link-underline">
          <ArrowLeft size={14} /> Back to {username}'s site
        </Link>
      </div>
    );
  }
  if (!data) return <div className="max-w-3xl mx-auto px-6 py-20 text-muted-foreground">Loading...</div>;

  const { article, author, has_liked, series_nav, related } = data;
  const accent = author?.theme?.accent || "#D32F2F";

  const toggleLike = async () => {
    if (!user || !user.id) {
      window.location.href = "/login";
      return;
    }
    setLiking(true);
    try {
      const { data: r } = await api.post(`/articles/${article.id}/like`);
      setData((d) => ({ ...d, has_liked: r.liked, article: { ...d.article, likes_count: r.likes_count } }));
    } finally { setLiking(false); }
  };

  const toggleBookmark = async () => {
    if (!user || !user.id) {
      window.location.href = "/login";
      return;
    }
    setBookmarking(true);
    try {
      const { data: r } = await api.post(`/articles/${article.id}/bookmark`);
      setData((d) => ({ ...d, has_bookmarked: r.bookmarked }));
    } finally { setBookmarking(false); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!user || !user.id) {
      window.location.href = "/login";
      return;
    }
    if (!newComment.trim()) return;
    setBusyComment(true);
    try {
      const { data: c } = await api.post(`/articles/${article.id}/comments`, { content: newComment });
      setComments((cs) => [c, ...cs]);
      setNewComment("");
    } finally { setBusyComment(false); }
  };

  const removeComment = async (id) => {
    if (!window.confirm("Delete comment?")) return;
    await api.delete(`/comments/${id}`);
    setComments((cs) => cs.filter((c) => c.id !== id));
  };

  return (
    <article>
      <Helmet>
        <title>{`${article.title} — ${author.name} | Inkspot`}</title>
        <meta name="description" content={article.excerpt || `Read "${article.title}" by ${author.name} on Inkspot.`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt || `Read "${article.title}" by ${author.name} on Inkspot.`} />
        {article.cover_image && <meta property="og:image" content={fileUrl(article.cover_image)} />}
        <meta property="og:url" content={window.location.href} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt || `Read "${article.title}" by ${author.name} on Inkspot.`} />
        {article.cover_image && <meta name="twitter:image" content={fileUrl(article.cover_image)} />}

        {/* Structured Data (JSON-LD) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": article.title,
            "description": article.excerpt,
            "image": article.cover_image ? fileUrl(article.cover_image) : undefined,
            "author": {
              "@type": "Person",
              "name": author.name,
              "url": `${window.location.origin}/${author.username}`
            },
            "datePublished": article.created_at,
            "publisher": {
              "@type": "Organization",
              "name": "Inkspot",
              "logo": {
                "@type": "ImageObject",
                "url": `${window.location.origin}/logo.png`
              }
            }
          })}
        </script>
      </Helmet>

      <div className="max-w-3xl mx-auto px-6 pt-8">
        <Link to={`/${username}`} className="link-underline text-sm text-muted-foreground inline-flex items-center gap-2" data-testid="article-back-link">
          <ArrowLeft size={14} /> {author.site_title || `${author.name}'s Journal`}
        </Link>
      </div>

      {/* HEADER */}
      <header className="max-w-3xl mx-auto px-6 pt-6 pb-8">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 eyebrow text-[10px] sm:text-xs" style={{ color: accent }}>
          <span>{new Date(article.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</span>
          <span className="text-muted-foreground">·</span>
          <span>{article.reading_time || 1} min read</span>
          {article.category && (
            <>
              <span className="text-muted-foreground">·</span>
              <span className="uppercase tracking-widest">{article.category.name}</span>
              {article.sub_category && (
                <>
                  <span className="text-muted-foreground">·</span>
                  <span className="italic">{article.sub_category}</span>
                </>
              )}
            </>
          )}
        </div>
        <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mt-3" data-testid="article-title">
          {article.title}
        </h1>
        {article.excerpt ? (
          <p className="mt-4 text-xl italic font-serif text-muted-foreground border-l-2 pl-4" style={{ borderColor: accent }}>
            {article.excerpt}
          </p>
        ) : null}
        <div className="mt-6 flex items-center justify-between">
          <Link to={`/${author.username}`} className="flex items-center gap-3 group">
            {author.profile_picture ? (
              <img src={fileUrl(author.profile_picture)} alt="" className="h-10 w-10 object-cover" />
            ) : (
              <div className="h-10 w-10 bg-secondary grid place-items-center font-serif text-lg">{(author.name || "A")[0]}</div>
            )}
            <div>
              <div className="text-sm font-medium link-underline">{author.name}</div>
              <div className="text-xs text-muted-foreground">@{author.username}</div>
            </div>
          </Link>
          <div className="flex items-center gap-5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Eye size={14} /> {article.views}</span>
            <span className="flex items-center gap-1"><Heart size={14} /> {article.likes_count}</span>
            <span className="flex items-center gap-1"><ChatCircle size={14} /> {comments.length}</span>
          </div>
        </div>
      </header>

      {article.cover_image ? (
        <div className="max-w-5xl mx-auto px-6 mb-8">
          <img src={fileUrl(article.cover_image)} alt="" className="w-full max-h-[80vh] object-cover border border-border" />
        </div>
      ) : null}

      {/* BODY */}
      <div className="max-w-3xl mx-auto px-6 article-prose" data-testid="article-body" dangerouslySetInnerHTML={{ __html: article.content }} />

      {series_nav && (
        <div className="max-w-3xl mx-auto px-6 mt-12">
          <div className="border border-foreground bg-foreground text-background p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 eyebrow text-[10px] text-background/80">
                <Books size={14} /> Part of series: <Link to={`/${username}/series/${series_nav.series_slug}`} className="underline font-bold text-background">{series_nav.series_title}</Link>
              </div>
              <div className="text-[10px] font-mono">
                {series_nav.current_index + 1} / {series_nav.total}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {series_nav.prev ? (
                <Link to={`/${username}/${series_nav.prev.slug}`} className="group p-3 border border-background/20 hover:bg-background/10 text-left transition-colors">
                  <div className="text-[10px] eyebrow text-background/60 mb-1 flex items-center gap-1">
                    <ArrowLeft size={10} /> Previous
                  </div>
                  <div className="text-sm font-serif font-bold line-clamp-1 group-hover:underline">
                    {series_nav.prev.title}
                  </div>
                </Link>
              ) : <div />}
              
              {series_nav.next ? (
                <Link to={`/${username}/${series_nav.next.slug}`} className="group p-3 border border-background/20 hover:bg-background/10 text-right transition-colors">
                  <div className="text-[10px] eyebrow text-background/60 mb-1 flex items-center justify-end gap-1">
                    Next <ArrowRight size={10} />
                  </div>
                  <div className="text-sm font-serif font-bold line-clamp-1 group-hover:underline">
                    {series_nav.next.title}
                  </div>
                </Link>
              ) : <div />}
            </div>

            <div className="mt-4 pt-4 border-t border-background/20">
              <Link to={`/${username}/series/${series_nav.series_slug}`} className="text-[10px] eyebrow text-background hover:underline block text-center">
                View Full Playlist ({series_nav.total} pieces)
              </Link>
            </div>
          </div>
        </div>
      )}


      {/* LIKE BAR */}
    <div className="max-w-3xl mx-auto px-6 mt-8 border-y border-border py-5 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <button onClick={toggleLike} disabled={liking}
          aria-label={has_liked ? "Unlike article" : "Like article"}
          className={`inline-flex items-center gap-2 px-5 py-3 border transition-colors ${has_liked ? "bg-foreground text-background border-foreground" : "border-foreground hover:bg-foreground/5"}`}
          data-testid="article-like-button"
        >
          <Heart size={16} weight={has_liked ? "fill" : "regular"} /> {has_liked ? "Liked" : "Like"} · {article.likes_count}
        </button>
        <button onClick={toggleBookmark} disabled={bookmarking}
          aria-label={data.has_bookmarked ? "Remove bookmark" : "Bookmark article"}
          className={`inline-flex items-center gap-2 px-5 py-3 border transition-colors ${data.has_bookmarked ? "bg-yellow-500/10 border-yellow-500 text-yellow-700" : "border-border hover:bg-foreground/5"}`}
          data-testid="article-bookmark-button"
        >
          <BookmarkSimple size={16} weight={data.has_bookmarked ? "fill" : "regular"} />
          {data.has_bookmarked ? "Saved" : "Save"}
        </button>
      </div>
      <div className="text-sm text-muted-foreground hidden sm:block">Enjoyed this? <a href={`/${author.username}`} className="link-underline text-foreground">Read more from {author.name}</a></div>
    </div>

      {/* SUBSCRIBE */}
      <section className="max-w-3xl mx-auto px-6">
        <NewsletterSignup username={author.username} accent={accent} />
      </section>

      {/* COMMENTS */}
      <section className="max-w-3xl mx-auto px-6 mt-8 mb-16">
        <GoogleAd />
        <div className="eyebrow text-muted-foreground mb-4">Comments · {comments.length}</div>
        <form onSubmit={submitComment} className="border border-border bg-card p-4 mb-6" data-testid="comment-form">
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={user && user.id ? "Share a thought..." : "Sign in to comment."}
            disabled={!user || !user.id}
            rows={3}
            className="rounded-none border-0 focus-visible:ring-0 bg-transparent p-0 resize-none"
            data-testid="comment-input"
          />
          <div className="flex justify-end mt-2">
            <Button type="submit" disabled={busyComment || !newComment.trim() || !user || !user.id} className="rounded-none bg-foreground text-background hover:bg-foreground/90" data-testid="comment-submit-button">
              <PaperPlaneTilt size={14} className="mr-1.5" /> Post
            </Button>
          </div>
        </form>

        <ul className="space-y-6">
          {comments.map((c) => (
            <li key={c.id} className="flex items-start gap-3" data-testid={`comment-${c.id}`}>
              {c.author?.profile_picture ? (
                <img src={fileUrl(c.author.profile_picture)} alt="" className="h-9 w-9 object-cover" />
              ) : (
                <div className="h-9 w-9 bg-secondary grid place-items-center font-serif">{(c.author?.name || "A")[0]}</div>
              )}
              <div className="flex-1">
                <div className="text-sm">
                  <span className="font-medium">{c.author?.name || "Anonymous"}</span>
                  <span className="text-muted-foreground ml-2 font-mono text-xs">@{c.author?.username}</span>
                  <span className="text-muted-foreground ml-2 text-xs">· {new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm mt-1 text-foreground/90">{c.content}</p>
              </div>
              {user && (user.id === c.user_id || user.username === author.username) && (
                <button onClick={() => removeComment(c.id)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`comment-delete-${c.id}`}>
                  <Trash size={14} />
                </button>
              )}
            </li>
          ))}
          {comments.length === 0 && <li className="text-sm text-muted-foreground">No comments yet — be the first.</li>}
        </ul>
      </section>

      {related?.length ? (
        <section className="max-w-5xl mx-auto px-6 pb-12">
          <div className="border-t border-border pt-8 mb-6 eyebrow text-muted-foreground">More essays</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {related.map((r) => (
              <Link key={r.id} to={`/${author.username}/${r.slug}`} className="group block" data-testid={`related-${r.slug}`}>
                {r.cover_image ? (
                  <div className="aspect-[4/3] border border-border overflow-hidden">
                    <img src={fileUrl(r.cover_image)} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  </div>
                ) : (
                  <div className="aspect-[4/3] bg-secondary border border-border" />
                )}
                <div className="eyebrow text-muted-foreground mt-4">{new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                <h4 className="font-serif text-xl font-bold mt-1 leading-tight group-hover:underline">{r.title}</h4>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );
}
